import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FirestoreProjectService } from './firestore-project.service';
import { Project } from '../models/project.model';

/**
 * Centralized real-time analytics for the PPMS dashboard ribbon.
 *
 * Subscribes once to FirestoreProjectService.getProjects$() (a live `collectionData()` listener
 * under the hood) and exposes derived counts as Angular Signals. Every consumer reads the same
 * stream — no per-page Firestore subscriptions, no polling, no manual refresh.
 *
 * Auto-refresh triggers:
 *   • Minister edits a project           → updateProject → snapshot → signals re-fire
 *   • Operator updates progress / status → snapshot → signals re-fire
 *   • Approver flips approvalStatus      → snapshot → signals re-fire
 *   • HM flag / delay / progress edits   → snapshot → signals re-fire
 */
@Injectable({ providedIn: 'root' })
export class DashboardStatsService {
  private firestoreService = inject(FirestoreProjectService);

  /** Live project list — single source of truth for the entire app. */
  readonly projects = signal<Project[]>([]);
  /** Last snapshot timestamp — used for the "Last Sync" indicator. */
  readonly lastSync = signal<Date>(new Date());
  /** True once the first snapshot has arrived. */
  readonly isLive   = signal<boolean>(false);

  // ── Tunable thresholds (kept centralized) ──────────────────────────────
  private readonly LOW_PROGRESS_PCT = 30;
  private readonly DELAY_DAYS       = 60;

  // ── HM ⇄ Recurring strict separation ───────────────────────────────────
  /** True if the project should appear in HM Priorities. */
  static isHmProject(p: Project): boolean {
    if (!!p.recurringIntervention) return false; // strict mutex
    return !!p.hmPriority || (p as any).isHMProject === true || p.visibility === 'HM Committed';
  }
  /** True if the project should appear in Recurring Intervention. */
  static isRecurringProject(p: Project): boolean {
    if (!!p.hmPriority) return false; // strict mutex
    return !!p.recurringIntervention || (p as any).isRecurringIntervention === true;
  }

  /** Strict HM project list — excludes anything flagged Recurring. */
  readonly hmProjects = computed<Project[]>(() =>
    this.projects().filter(p => DashboardStatsService.isHmProject(p))
  );

  /** Strict Recurring Intervention list — excludes anything flagged HM. */
  readonly recurringProjects = computed<Project[]>(() =>
    this.projects().filter(p => DashboardStatsService.isRecurringProject(p))
  );

  // ── HM Priorities ribbon counters ──────────────────────────────────────
  readonly hmTotal       = computed(() => this.hmProjects().length);
  readonly hmCompleted   = computed(() => this.hmProjects().filter(p => p.status === 'Completed').length);
  readonly hmInProgress  = computed(() => this.hmProjects().filter(p => p.status === 'In Progress').length);
  readonly hmDelayedList = computed<Project[]>(() => {
    const today = Date.now();
    return this.hmProjects().filter(p => {
      if (p.status === 'Completed') return false;
      if (p.isDelayed) return true;
      if (p.status === 'Stuck') return true;
      const t = p.targetDate || p.end;
      if (!t) return false;
      return Math.floor((today - new Date(t).getTime()) / 86400000) > this.DELAY_DAYS;
    });
  });
  readonly hmDelayed       = computed(() => this.hmDelayedList().length);
  readonly hmLowProgress   = computed(() =>
    this.hmProjects().filter(p =>
      p.status !== 'Completed' && p.status !== 'Planned' && (p.physical ?? 0) < this.LOW_PROGRESS_PCT
    ).length
  );
  readonly hmPendingReview = computed(() =>
    this.hmProjects().filter(p => {
      const explicit = (p as any).approvalStatus === 'pending';
      const inferred = p.status !== 'Completed' && (p.physical ?? 0) < 50;
      return explicit || inferred;
    }).length
  );
  readonly hmCriticalAlerts = computed(() => {
    const ids = new Set<string>();
    this.hmDelayedList().forEach(p => ids.add(p.id));
    this.hmProjects()
      .filter(p => p.status === 'Stuck' || (p as any).approvalStatus === 'pending')
      .forEach(p => ids.add(p.id));
    return ids.size;
  });

  /** HM completion ratio for donut/progress charts. */
  readonly hmStatusBreakdown = computed(() => {
    const list = this.hmProjects();
    const tot  = list.length || 1;
    const completed  = list.filter(p => p.status === 'Completed').length;
    const inProgress = list.filter(p => p.status === 'In Progress').length;
    const stuck      = list.filter(p => p.status === 'Stuck').length;
    const planned    = list.filter(p => p.status === 'Planned').length;
    return {
      completed,  completedPct:  Math.round((completed  / tot) * 100),
      inProgress, inProgressPct: Math.round((inProgress / tot) * 100),
      stuck,      stuckPct:      Math.round((stuck      / tot) * 100),
      planned,    plannedPct:    Math.round((planned    / tot) * 100),
    };
  });

  /** Department-wise HM rollup for the analytics grid. */
  readonly hmByDepartment = computed(() => {
    const map = new Map<string, { dept: string; total: number; completed: number; delayed: number }>();
    const delayedSet = new Set(this.hmDelayedList().map(p => p.id));
    for (const p of this.hmProjects()) {
      const dept = p.dept || 'Unknown';
      const cur  = map.get(dept) ?? { dept, total: 0, completed: 0, delayed: 0 };
      cur.total++;
      if (p.status === 'Completed') cur.completed++;
      if (delayedSet.has(p.id))     cur.delayed++;
      map.set(dept, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  });

  // ── Derived counters (all live signals) ────────────────────────────────

  /**
   * HM Pending: HM-flagged projects awaiting Minister approval.
   * Treats either `approvalStatus === 'pending'` (when present) or
   * `hmPriority && status !== 'Completed' && physical < 50` as pending.
   */
  readonly hmPendingProjects = computed<Project[]>(() =>
    this.projects().filter(p => {
      const isHM = !!p.hmPriority || (p as any).isHMProject === true;
      if (!isHM) return false;
      const explicitPending = (p as any).approvalStatus === 'pending';
      const inferredPending = p.status !== 'Completed' && (p.physical ?? 0) < 50;
      return explicitPending || inferredPending;
    })
  );
  readonly hmPendingCount = computed(() => this.hmPendingProjects().length);

  /** Low progress: physical < 30% and not Completed/Planned. */
  readonly lowProgressProjects = computed<Project[]>(() =>
    this.projects().filter(p =>
      p.status !== 'Completed' &&
      p.status !== 'Planned' &&
      (p.physical ?? 0) < this.LOW_PROGRESS_PCT
    )
  );
  readonly lowProgressCount = computed(() => this.lowProgressProjects().length);

  /**
   * Delayed projects — consistent with the Department Status table criteria:
   *   • isDelayed === true  (OSD-flagged), OR
   *   • status === 'Stuck', OR
   *   • overdue > 60 days past targetDate / end
   * Excludes Completed projects.
   */
  readonly delayedProjects = computed<Project[]>(() => {
    const today = Date.now();
    return this.projects().filter(p => {
      if (p.status === 'Completed') return false;
      if (p.isDelayed) return true;
      if (p.status === 'Stuck') return true;
      const days = this.computeDelayDays(p, today);
      return days > this.DELAY_DAYS;
    });
  });
  readonly delayedCount = computed(() => this.delayedProjects().length);

  /** OSD-flagged delayed: ONLY projects where isDelayed === true (no date math). */
  readonly explicitlyDelayedProjects = computed<Project[]>(() =>
    this.projects().filter(p => !!p.isDelayed && p.status !== 'Completed')
  );
  readonly explicitlyDelayedCount = computed(() => this.explicitlyDelayedProjects().length);

  // ── Delayed project API methods ─────────────────────────────────────────

  /** Returns live list of projects explicitly marked delayed by OSD. */
  getDelayedProjects(): Project[] {
    return this.explicitlyDelayedProjects();
  }

  /** Returns live count of projects explicitly marked delayed by OSD. */
  getDelayedProjectCount(): number {
    return this.explicitlyDelayedCount();
  }

  /** Mark a project delayed. Persists isDelayed=true + metadata to Firestore. */
  async markProjectDelayed(
    projectId: string,
    reason: string,
    markedBy: string
  ): Promise<void> {
    await this.firestoreService.updateProject(projectId, {
      isDelayed:    true,
      delayedReason: reason,
      delayedDate:  new Date().toISOString().split('T')[0],
      delayedBy:    markedBy,
    } as any);
  }

  /** Remove the delayed flag from a project. Keeps audit fields for history. */
  async removeDelayedFlag(projectId: string): Promise<void> {
    await this.firestoreService.updateProject(projectId, { isDelayed: false } as any);
  }

  /** Aggregate "system health" view used by the System Status card. */
  readonly systemHealth = computed(() => {
    const total       = this.projects().length;
    const isLive      = this.isLive();
    const lastSync    = this.lastSync();
    const hasAlerts   = this.delayedCount() > 0 || this.hmPendingCount() > 0;
    const status: 'active' | 'idle' | 'offline' =
      !isLive ? 'offline' : (total === 0 ? 'idle' : 'active');
    return { total, isLive, lastSync, hasAlerts, status };
  });

  constructor() {
    this.firestoreService.getHmProjects$()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: list => {
          this.projects.set(list);
          this.lastSync.set(new Date());
          this.isLive.set(true);
        },
        error: err => {
          console.warn('[DashboardStatsService] Firestore stream error:', err);
          this.isLive.set(false);
        }
      });

    // Triggers legacy cleanup once real data arrives (handled inside getProjects$).
    this.firestoreService.getProjects$()
      .pipe(takeUntilDestroyed())
      .subscribe({ error: () => { /* non-fatal */ } });
  }

  private computeDelayDays(p: Project, todayMs: number): number {
    const t = p.targetDate || p.end;
    if (!t) return 0;
    const tMs = new Date(t).getTime();
    if (isNaN(tMs)) return 0;
    return Math.floor((todayMs - tMs) / 86400000);
  }

  /**
   * Persist the HM/Recurring flags with strict mutual exclusion.
   *
   * Always writes BOTH fields so `hmPriority === !recurringIntervention` is preserved
   * — even if the caller forgot to clear the opposite flag.
   *
   * @param mode 'hm'        → hmPriority=true,  recurringIntervention=false
   *             'recurring' → hmPriority=false, recurringIntervention=true
   *             'none'      → both false (e.g. when minister demotes a project)
   */
  async setProjectClassification(
    projectId: string,
    mode: 'hm' | 'recurring' | 'none'
  ): Promise<void> {
    const updates = {
      hmPriority:            mode === 'hm',
      recurringIntervention: mode === 'recurring',
    };
    await this.firestoreService.updateProject(projectId, updates);
  }

  /** Convenience: derive the canonical mode from a Project. */
  static classificationOf(p: Project): 'hm' | 'recurring' | 'none' {
    if (DashboardStatsService.isHmProject(p))        return 'hm';
    if (DashboardStatsService.isRecurringProject(p)) return 'recurring';
    return 'none';
  }
}
